
import React, { useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactDialog from './ContactDialog';
import { useIsMobile } from '@/hooks/use-mobile';

const Hero: React.FC = () => {
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const parallaxElements = document.querySelectorAll('.parallax');
      const mouseX = e.clientX / window.innerWidth - 0.5;
      const mouseY = e.clientY / window.innerHeight - 0.5;

      parallaxElements.forEach((el, index) => {
        const depth = index + 1;
        const moveX = mouseX * depth * 20;
        const moveY = mouseY * depth * 20;
        (el as HTMLElement).style.transform = `translate(${moveX}px, ${moveY}px)`;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gray-900 text-white">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden bg-[#010b20]">
        {/* Animated wave element */}
        <div className="absolute w-full h-full overflow-hidden">
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent transform -translate-y-1/2 animate-wave"></div>
          <div className="absolute top-[45%] left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent transform -translate-y-1/2 animate-wave-slow"></div>
          <div className="absolute top-[55%] left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-300/20 to-transparent transform -translate-y-1/2 animate-wave-slower"></div>
          
          {/* Glowing point that emits the waves */}
          <div className="absolute top-1/2 left-[20%] w-2 h-2 rounded-full bg-white/90 shadow-[0_0_30px_15px_rgba(59,130,246,0.5)] animate-pulse-gentle transform -translate-y-1/2"></div>
          
          {/* Radial rays emanating from the glowing point */}
          <div className="absolute top-1/2 left-[20%] w-20 h-20 animate-rays-rotate transform -translate-x-1/2 -translate-y-1/2">
            {Array.from({ length: 12 }).map((_, index) => (
              <div 
                key={index} 
                className="absolute top-1/2 left-1/2 h-[1px] w-10 bg-gradient-to-r from-blue-400/90 to-transparent origin-left"
                style={{ transform: `rotate(${index * 30}deg)` }}
              ></div>
            ))}
          </div>
        </div>

        {/* Original parallax elements */}
        <div className="absolute top-20 right-[10%] w-64 h-64 bg-primary/10 rounded-full filter blur-3xl parallax"></div>
        <div className="absolute bottom-20 left-[15%] w-96 h-96 bg-secondary/10 rounded-full filter blur-3xl parallax"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-accent/10 rounded-full filter blur-3xl parallax"></div>
      </div>

      <div className="container relative z-10 mx-auto px-6 md:px-10 flex flex-col items-center text-center pt-28 md:pt-20">
        {/* Title with Logo - Only visible on desktop */}
        {!isMobile && (
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/lovable-uploads/2a4ca809-1e4b-4f11-9936-d9e370f05626.png" 
              alt="Sparkwave AI Logo" 
              className="h-16 md:h-20 mr-3" 
            />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-glacial text-white tracking-wide">SPARKWAVE AI</h1>
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 max-w-4xl leading-tight md:leading-tight lg:leading-tight mt-16 md:mt-0">
          <span className="text-gradient-accent">We Build AI.</span><br />
          <span className="text-white">You Grow Your Business.</span>
        </h1>
        
        <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl">
          We build AI solutions that automate your operations, understand your market, and accelerate your growth.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <ContactDialog 
            trigger={
              <Button size="lg" className="btn-hover-effect group bg-heading-accent hover:bg-heading-accent/90 text-white">
                Book a Free Consultation
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            }
            title="Book a Free Consultation"
            description="Fill out the form below and we'll get back to you within 24 hours."
          />
          <Button variant="outline" size="lg" className="bg-transparent hover:bg-white/10 text-white border-white/20" asChild>
            <Link to="/services">Explore Our Services</Link>
          </Button>
        </div>
        
        {/* Logo floating element */}
        <div className="absolute top-1/4 right-[20%] w-20 h-20 opacity-30 animate-float parallax hidden md:block">
          <img src="/lovable-uploads/2a4ca809-1e4b-4f11-9936-d9e370f05626.png" alt="Sparkwave AI Icon" className="w-full h-full" />
        </div>
        <div className="absolute bottom-1/3 left-[15%] w-12 h-12 opacity-20 animate-float parallax hidden md:block" style={{ animationDelay: "1.5s" }}>
          <img src="/lovable-uploads/2a4ca809-1e4b-4f11-9936-d9e370f05626.png" alt="Sparkwave AI Icon" className="w-full h-full" />
        </div>
      </div>
      
      {/* Gradient overlay at the bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent"></div>
    </section>
  );
};

export default Hero;
